import { useEffect, useState, type Dispatch } from 'react';
import debounce from 'lodash/debounce';
import { getIdeasCountForCollection } from './updateCollection.tsx';
import { getJQLForCollection } from './getJQLForCollection.tsx';

const withQueryParams = <T extends Record<string, string>>(path: string, queryParams?: T) => {
	if (queryParams) {
		const searchParams = new URLSearchParams();
		Object.keys(queryParams).forEach((key) => {
			if (queryParams[key]) {
				searchParams.set(key, queryParams[key]);
			}
		});
		if (searchParams.size > 0) {
			return `${path}?${searchParams.toString()}`;
		}
	}

	return path;
};

export const getCollectionDetailsPageUrl = (collectionUUID: string): string =>
	`/jira/polaris/roadmaps/${collectionUUID}/view`;

type CollectionViewPageQueryParams = {
	sidebar?: 'about' | 'settings';
};

export const getCollectionViewPageUrl = (
	collectionUUID: string,
	viewUUID: string,
	queryParams?: CollectionViewPageQueryParams,
): string =>
	withQueryParams(`/jira/polaris/roadmaps/${collectionUUID}/view/${viewUUID}`, queryParams);

export const getCollectionDetailsPageUrlWithQuery = (
	collectionUUID: string,
	queryParams?: CollectionViewPageQueryParams,
): string => withQueryParams(`/jira/polaris/roadmaps/${collectionUUID}/view`, queryParams);

type CollectionsPageQueryParams = {
	sidebar?: 'about' | 'settings';
	roadmap?: string;
};

export const getCollectionsPageUrl = (queryParams?: CollectionsPageQueryParams): string =>
	withQueryParams('/jira/polaris/roadmaps', queryParams);

type IdeaCountRequest = Promise<number> | undefined;

const debouncedGetIdeasCountWithJQL = debounce(
	(
		jql: string,
		setRequest: Dispatch<IdeaCountRequest>,
		res: (count: number) => void,
		rej?: (err: Error) => void,
	) => {
		const request = getIdeasCountForCollection(jql);
		setRequest(request);
		request.then(res, rej).finally(() => setRequest(undefined));
	},
	500,
);

export const useCollectionIdeasCount = (
	projectKeys: string[],
	filtersJql?: string,
): [undefined | number, boolean, Error | null, IdeaCountRequest] => {
	const jql = getJQLForCollection(projectKeys, filtersJql);
	const [ideasCount, setIdeasCount] = useState<undefined | number>(undefined);
	const [isLoading, setLoading] = useState(false);
	const [error, setError] = useState<null | Error>(null);
	const [request, setRequest] = useState<IdeaCountRequest>(undefined);

	useEffect(() => {
		if (jql && projectKeys.length) {
			setLoading(true);
			debouncedGetIdeasCountWithJQL(
				jql,
				setRequest,
				(newCount) => {
					setIdeasCount(newCount);
					setLoading(false);
					setError(null);
				},
				(err: Error) => {
					setIdeasCount(undefined);
					setLoading(false);
					setError(err);
				},
			);
		} else {
			setIdeasCount(undefined);
		}
	}, [jql, projectKeys.length]);

	return [ideasCount, isLoading, error, request];
};
